// Board templates (spec §12, phase 6). Generated fresh each time (new ids),
// so the server can seed a new board's first snapshot and the client can draw
// previews from the same source.
import type { BoardObject, Card, ChecklistItem, Endpoint, RichText, ShapeKind } from './types.ts';
import { cardWidthIn, stack } from './columns.ts';
import { keysBetween } from './fractional-index.ts';
import { newId } from './ids.ts';
import { singleLineCardHeight } from './cards.ts';

export type TemplateId = 'blank' | 'kanban' | 'retro' | 'brainstorm' | 'flowchart';

export const TEMPLATES: readonly { id: TemplateId; name: string; description: string }[] = [
  { id: 'blank', name: 'Blank board', description: 'Start from an empty canvas.' },
  { id: 'kanban', name: 'Kanban', description: 'Cards with checklists in to do, doing and done columns.' },
  { id: 'retro', name: 'Retrospective', description: 'What went well, what to improve, actions.' },
  { id: 'brainstorm', name: 'Brainstorm', description: 'A central idea with prompts around it.' },
  { id: 'flowchart', name: 'Flowchart', description: 'Start, steps, a decision and an end.' },
];

export const isTemplateId = (v: unknown): v is TemplateId => TEMPLATES.some((t) => t.id === v);

const YELLOW = '#FFE066', PINK = '#FFB3C7', GREEN = '#A8E6CF', BLUE = '#9AD0F5', PEACH = '#FFCB8E';
type Anchor = [number, number];
const TOP: Anchor = [0.5, 0], RIGHT: Anchor = [1, 0.5], BOTTOM: Anchor = [0.5, 1], LEFT: Anchor = [0, 0.5];

const rt = (text: string, bold = false): RichText => ({
  blocks: text.split('\n').map((t) => ({ runs: [bold ? { t, b: 1 as const } : { t }] })),
});

/** Collects objects; frames are stacked beneath everything else. */
class Builder {
  private readonly frames: BoardObject[] = [];
  private readonly rest: BoardObject[] = [];
  private readonly by: string;

  constructor(createdBy: string) {
    this.by = createdBy;
  }

  private base(x: number, y: number, w: number, h: number) {
    return { id: newId(), x, y, w, h, rotation: 0, z: '', createdBy: this.by };
  }

  sticky(x: number, y: number, text: string, color: string, size = 240): BoardObject {
    const o: BoardObject = { ...this.base(x, y, size, size), type: 'sticky', color, text: rt(text) };
    this.rest.push(o);
    return o;
  }

  shape(kind: ShapeKind, x: number, y: number, w: number, h: number, text: string, fill = '#FFFFFF'): BoardObject {
    const o: BoardObject = { ...this.base(x, y, w, h), type: 'shape', kind, fill, stroke: '#1A1A1A', strokeWidth: 2, text: rt(text) };
    this.rest.push(o);
    return o;
  }

  text(x: number, y: number, w: number, text: string, fontSize: number, bold = false): BoardObject {
    const o: BoardObject = { ...this.base(x, y, w, fontSize * 1.3), type: 'text', fontSize, text: rt(text, bold) };
    this.rest.push(o);
    return o;
  }

  /** A card whose title and items each fit on one line (so its height is exact without measuring). */
  card(x: number, y: number, w: number, title: string, color: string, items: [string, boolean][] = []): BoardObject {
    const orders = keysBetween(null, null, items.length);
    const list: Record<string, ChecklistItem> = {};
    items.forEach(([text, done], i) => { list[newId()] = { text, done, order: orders[i]! }; });
    const o: Card = { ...this.base(x, y, w, 0), type: 'card', title, color, items: list, assignees: [] };
    o.h = singleLineCardHeight(o);
    this.rest.push(o);
    return o;
  }

  frame(x: number, y: number, w: number, h: number, title: string, children: BoardObject[], layout?: 'column'): BoardObject {
    const o: BoardObject = {
      ...this.base(x, y, w, h), type: 'frame', title, children: children.map((c) => c.id), ...(layout ? { layout } : {}),
    };
    this.frames.push(o);
    return o;
  }

  connect(a: BoardObject, fromAnchor: Anchor, b: BoardObject, toAnchor: Anchor, opts: { routing?: 'straight' | 'elbow'; arrow?: boolean } = {}): BoardObject {
    const from: Endpoint = { objectId: a.id, anchor: fromAnchor };
    const to: Endpoint = { objectId: b.id, anchor: toAnchor };
    // Stored bounds are only a fallback if an end's object disappears.
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    const o: BoardObject = {
      ...this.base(x, y, Math.max(a.x + a.w, b.x + b.w) - x, Math.max(a.y + a.h, b.y + b.h) - y),
      type: 'connector', from, to, routing: opts.routing ?? 'elbow', stroke: '#1A1A1A', arrowStart: false, arrowEnd: opts.arrow ?? true,
    };
    this.rest.push(o);
    return o;
  }

  build(): BoardObject[] {
    const all = [...this.frames, ...this.rest];
    const zs = keysBetween(null, null, all.length);
    return all.map((o, i) => ({ ...o, z: zs[i]! }));
  }
}

function columns(b: Builder, title: string, cols: { title: string; color: string; notes: string[] }[]): void {
  const FRAME_W = 800, FRAME_H = 1100, GAP = 80;
  b.text(0, -160, 1400, title, 56, true);
  cols.forEach((col, i) => {
    const fx = i * (FRAME_W + GAP);
    const notes = col.notes.map((n, j) => b.sticky(fx + 120 + (j % 2) * 320, 80 + Math.floor(j / 2) * 320, n, col.color));
    b.frame(fx, 0, FRAME_W, FRAME_H, col.title, notes);
  });
}

const BLUE_CARD = '#4262FF', GREEN_CARD = '#30A46C', ORANGE_CARD = '#F76B15', PURPLE_CARD = '#8E4EC6';

/** Kanban (spec §13.4, §13.6): columns that stack their cards; a card's column is its status. */
function kanban(b: Builder): void {
  const COL_W = 380, GAP = 40;
  b.text(0, -160, 1200, 'Project board', 56, true);
  const cols: { title: string; cards: [string, string, [string, boolean][]][] }[] = [
    { title: 'To do', cards: [
      ['Research competitors', BLUE_CARD, [['List the top five', false], ['Compare pricing', false]]],
      ['Draft onboarding copy', PURPLE_CARD, []],
      ['Plan user interviews', ORANGE_CARD, [['Write the script', false], ['Book five sessions', false], ['Send thank-yous', false]]],
    ] },
    { title: 'In progress', cards: [
      ['Design review', PURPLE_CARD, [['Share the mockups', true], ['Collect feedback', false]]],
      ['Build the prototype', BLUE_CARD, [['Set up the project', true], ['Main flow', false]]],
    ] },
    { title: 'Done', cards: [
      ['Kick-off meeting', GREEN_CARD, [['Agree the goals', true], ['Set the timeline', true]]],
    ] },
  ];
  const cardW = cardWidthIn(COL_W);
  const laid = cols.map((col, i) => {
    const x = i * (COL_W + GAP);
    const cards = col.cards.map(([title, color, items]) => b.card(x + (COL_W - cardW) / 2, 0, cardW, title, color, items));
    const { tops, height } = stack(0, cards.map((c) => c.h));
    cards.forEach((c, j) => { c.y = tops[j]!; });
    return { x, title: col.title, cards, height };
  });
  // A row of columns shares the tallest one's height.
  const height = Math.max(...laid.map((c) => c.height));
  for (const col of laid) b.frame(col.x, 0, COL_W, height, col.title, col.cards, 'column');
}

export function templateObjects(id: TemplateId, createdBy: string): BoardObject[] {
  const b = new Builder(createdBy);
  switch (id) {
    case 'blank':
      break;
    case 'kanban':
      kanban(b);
      break;
    case 'retro':
      columns(b, 'Sprint retrospective', [
        { title: 'Went well', color: GREEN, notes: ['Shipped on time', 'Great pairing sessions'] },
        { title: 'To improve', color: PINK, notes: ['Too many meetings', 'Flaky tests'] },
        { title: 'Action items', color: YELLOW, notes: ['Fix CI by Friday', 'Try no-meeting Wednesdays'] },
      ]);
      break;
    case 'brainstorm': {
      b.text(-500, -1000, 1000, 'Brainstorm', 56, true);
      const hub = b.shape('ellipse', -220, -120, 440, 240, 'Central idea', '#E8F0FE');
      const prompts = ['Who is it for?', 'What problem does it solve?', 'How might we…?', 'Wild idea',
        'Quick win', 'Risks', 'Open questions', 'Next steps'];
      const colors = [YELLOW, PINK, GREEN, BLUE, PEACH];
      prompts.forEach((p, i) => {
        const a = (i / prompts.length) * Math.PI * 2 - Math.PI / 2;
        const cx = Math.cos(a) * 760, cy = Math.sin(a) * 520;
        const note = b.sticky(cx - 120, cy - 120, p, colors[i % colors.length]!);
        const horizontal = Math.abs(Math.cos(a)) * 760 > Math.abs(Math.sin(a)) * 520;
        const out: Anchor = horizontal ? (Math.cos(a) > 0 ? RIGHT : LEFT) : (Math.sin(a) > 0 ? BOTTOM : TOP);
        const into: Anchor = horizontal ? (Math.cos(a) > 0 ? LEFT : RIGHT) : (Math.sin(a) > 0 ? TOP : BOTTOM);
        b.connect(hub, out, note, into, { routing: 'straight', arrow: false });
      });
      break;
    }
    case 'flowchart': {
      b.text(-400, -220, 800, 'Process flow', 56, true);
      const start = b.shape('ellipse', -130, 0, 260, 110, 'Start', '#E6F4EA');
      const receive = b.shape('rect', -150, 210, 300, 130, 'Receive request');
      const decide = b.shape('diamond', -160, 440, 320, 200, 'Approved?', '#FEF7E0');
      const work = b.shape('rect', -150, 740, 300, 130, 'Do the work');
      const end = b.shape('ellipse', -130, 970, 260, 110, 'Done', '#FDECEA');
      const back = b.shape('rect', 360, 475, 300, 130, 'Send back with notes');
      b.connect(start, BOTTOM, receive, TOP);
      b.connect(receive, BOTTOM, decide, TOP);
      b.connect(decide, BOTTOM, work, TOP);
      b.connect(work, BOTTOM, end, TOP);
      b.connect(decide, RIGHT, back, LEFT);
      b.connect(back, TOP, receive, RIGHT);
      b.text(20, 660, 120, 'Yes', 24, true);
      b.text(190, 490, 120, 'No', 24, true);
      break;
    }
  }
  return b.build();
}
